import { Users } from '@platform/client';
import {
  GetCurrentUser200,
  OrganizationApplicationStatusEnum,
  ProfessionalApplicationStatusEnum,
} from '@platform/client/schemas';
import { ReactNode, useEffect, useRef, useState } from 'react';
import AuthedNavbar from './layout/AuthedNavbar';
import DashboardHeader from './layout/DashboardHeader';
import PageFooter from './layout/PageFooter';
import { UnauthedNavbar } from './layout/UnauthedNavbar';
import MetaTags from './util/MetaTags';

/**
 * A route wrapper that redirects users to the login page if they are not authed
 * or to the application if they have not completed signup
 **/
export const AuthedPage = ({ children }: { children: React.ReactElement }) => {
  const userData = Users.useGetCurrentUser({
    query: {
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      throwOnError: false,
    },
  });

  const isAuthed = userData.isSuccess;
  const isPending = userData.isPending;

  // Redirect to application if non-admin user is authed but has not completed signup and been approved
  const userAppRoute = userData.data?.applicationRoute;
  const applicationApproved = userApplicationApproved(userData.data);

  useEffect(() => {
    if (window) {
      const windowPathname = window.location.pathname;
      if (!isAuthed && !isPending) {
        window.location.href =
          'platform/auth/login?next=' + encodeURIComponent(windowPathname) + window.location.search;
      } else if (!applicationApproved && userAppRoute) {
        const locationIsAppRoute =
          window.location.href.includes(`application/${userAppRoute}`) ||
          window.location.href.includes('cohort-selection');
        if (!locationIsAppRoute) {
          window.location.href = `platform/application/${userAppRoute}`;
        }
      }
    }
  }, [isAuthed, isPending, userAppRoute, applicationApproved]);

  //NOTE: We wait for confirmation of auth before rendering because there is otherwise a race
  // condition between the useEffect to redirect and any children that depend on getting the current user throwing an error
  // which prevents the useEffect from firing properly
  if (isAuthed && !isPending) {
    return <>{children}</>;
  }

  return null;
};

export const HeaderFooterLayout = ({
  useDashboardHeader,
  children,
}: {
  useDashboardHeader?: boolean;
  children: ReactNode;
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // TODO: Currently, this component receives an error code when the user is unauthed. This is us
  // intentionally using error behavior to drive application logic instead of treating it as
  // an exception. We should tweak our API hooks to not error and instead describe that the user
  // is unauthed
  const userData = Users.useGetCurrentUser();
  const isAuthed = userData.isSuccess;
  useEffect(() => {
    const hideSidebar = (e: MouseEvent) => {
      if (isSidebarOpen && !e.composedPath().includes(sidebarRef.current as HTMLDivElement)) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('click', hideSidebar, true);
    return () => document.removeEventListener('click', hideSidebar, true);
  }, [isSidebarOpen]);

  return (
    <>
      {!isAuthed && (
        <UnauthedNavbar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          sidebarRef={sidebarRef}
        />
      )}
      {isAuthed && !useDashboardHeader && (
        <AuthedNavbar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          sidebarRef={sidebarRef}
          showDashboardDropdown={!useDashboardHeader}
        />
      )}
      {isAuthed && useDashboardHeader && (
        <>
          <div className='hidden md:block'>
            <DashboardHeader />
          </div>
          {/* TODO: The dashboard header doesn't work at mobile resolutions, so we fallback
           * to the non-dashboard header for mobile displays */}
          <div className='md:hidden'>
            <AuthedNavbar
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              sidebarRef={sidebarRef}
              showDashboardDropdown={!useDashboardHeader}
            />
          </div>
        </>
      )}
      <div
        id='root-scroll'
        className='flex flex-col overflow-x-clip overflow-y-auto h-[calc(100vh-54px)] md:h-[calc(100vh-var(--navbar-height))]'
      >
        <div className='flex-grow bg-gray-25 pt-12'>{children}</div>
        <div className='mt-auto'>
          <PageFooter />
        </div>
      </div>
    </>
  );
};

const App = ({ children }: { children: ReactNode }) => {
  return (
    <div>
      <MetaTags
        title='MovingWorlds - The Global Experteering Network'
        description='Find the best place to volunteer your skills, anywhere you want to travel.'
        imageUrl='https://assets.movingworlds.org/images/mwlogo-square-blue.png'
      />
      {children}
    </div>
  );
};

/** Helper function to check user and org app status, along with admin role to determine if a user has completed sign up
 * application and been approved.
 */
const userApplicationApproved = (userData: GetCurrentUser200 | undefined) => {
  if (!userData) return false;

  return (
    userData.role === 'admin' ||
    userData.applicationStatus === ProfessionalApplicationStatusEnum.approved ||
    (userData.accountType === 'organization' &&
      (userData.organization?.applicationStatus === OrganizationApplicationStatusEnum.approved ||
        userData.organization?.applicationStatus ===
          OrganizationApplicationStatusEnum.approved_match))
  );
};

export default App;
